import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Lock, ArrowLeft, Eye, EyeOff, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Заполните все поля");
      return;
    }

    setLoading(true);
    try {
      const { profile } = await login(email, password);
      const role = profile?.role || "client";
      if (role === "admin") {
        navigate("/admin");
      } else if (role === "expert") {
        navigate("/expert/dashboard");
      } else {
        navigate("/client/dashboard");
      }
    } catch (err) {
      const msg = err.message || "Ошибка входа";
      if (msg.includes("Invalid login credentials")) {
        setError("Неверный email или пароль");
      } else if (msg.includes("Email not confirmed")) {
        setError("Подтвердите email, перейдя по ссылке из письма");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="login_page" className="min-h-screen flex items-center justify-center px-4 py-12 bg-neutral-950">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-lg font-extralight">На главную</span>
        </Link>

        <div className="p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800 backdrop-blur-xl">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-xl">О</span>
              </div>
            </Link>
            <h1 className="text-2xl font-medium text-white mb-2">
              Вход в аккаунт
            </h1>
            <p className="text-lg font-extralight text-neutral-400">
              Рады видеть вас снова
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-lg font-extralight">
                {error}
              </div>
            )}

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Пароль
              </label>
              <div className="relative" style={{ position: "relative" }}>
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 pointer-events-none" style={{ position: "absolute", left: "1rem", top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                  style={{ width: "100%" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
                  style={{ position: "absolute", right: "1rem", top: "50%", transform: "translateY(-50%)" }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setRememberMe(!rememberMe)}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                    rememberMe
                      ? "bg-primary-600 border-primary-500"
                      : "bg-neutral-800 border-neutral-600 group-hover:border-neutral-500"
                  }`}
                >
                  {rememberMe && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </div>
                <span className="text-lg font-extralight text-neutral-400 group-hover:text-neutral-300 transition-colors">
                  Запомнить меня
                </span>
              </button>
              <Link
                to="/forgot-password"
                className="text-lg font-extralight text-primary-400 hover:text-primary-300 transition-colors"
              >
                Забыли пароль?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
            >
              {loading ? "Вход..." : "Войти"}
            </button>
          </form>

          <p className="text-center text-lg font-extralight text-neutral-400 mt-6">
            Нет аккаунта?{" "}
            <Link
              to="/register"
              className="text-primary-400 hover:text-primary-300 transition-colors"
            >
              Зарегистрироваться
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}